export interface User {
  id: number;
  username: string;
  email: string;
  fullName?: string;
  bio?: string;
  profilePhotoUrl?: string;
  credits: number;
  createdAt: string;
  updatedAt: string;
  enabled: boolean;
  blocked: boolean;
  deleted: boolean;
  roles: Role[];
}

export interface Role {
  id: number;
  name: string;
}

export interface Skill {
  id: number;
  name: string;
  description: string;
  category: SkillCategory;
  createdAt: string;
  updatedAt: string;
}

export interface SkillCategory {
  id: number;
  name: string;
  description: string;
}

export interface UserSkill {
  id: number;
  user: User;
  skill: Skill;
  relationType: SkillRelationType;
  proficiencyLevel: number;
  proficiencyDescription: string;
  // New fields for enhanced post information
  hourlyRate?: number;
  availability?: string;
  tags?: string[];
  rating?: number;
  totalSessions?: number;
  createdAt: string;
  updatedAt: string;
}

export enum SkillRelationType {
  TEACH = 'TEACH',
  LEARN = 'LEARN'
}

export interface SessionRequest {
  id: number;
  requester: User;
  recipient: User;
  teacher: User;
  learner: User;
  skill: Skill;
  status: SessionRequestStatus;
  proposedDateTime: string;
  durationMinutes: number;
  message: string;
  meetingLink?: string;
  createdAt: string;
  updatedAt: string;
}

export enum SessionRequestStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED'
}

export interface LearningSession {
  id: number;
  teacher: User;
  learner: User;
  skill: Skill;
  status: SessionStatus;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  creditProcessed: boolean;
  sessionRequest: SessionRequest;
  meetingLink?: string;
  meetingNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export enum SessionStatus {
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export interface ChatRoom {
  id: number;
  participants: User[];
  createdAt: string;
}

export interface ChatMessage {
  id: number;
  chatRoom: ChatRoom;
  sender: User;
  content: string;
  timestamp: string;
  messageType: 'TEXT' | 'FILE' | 'IMAGE';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
}

export interface Feedback {
  id: number;
  session: LearningSession;
  reviewer: User;
  reviewed: User;
  rating: number;
  comment: string;
  createdAt: string;
}

export interface CreditTransaction {
  id: number;
  user: User;
  amount: number;
  type: string;
  description: string;
  createdAt: string;
}

// Auth DTOs
export interface LoginRequest {
  username: string;
  password: string;
}

export interface SignupRequest {
  username: string;
  email: string;
  password: string;
  fullName?: string;
  bio?: string;
  roles?: string[];
}

export interface JwtResponse {
  token: string;
  refreshToken: string;
  type: string;
  id: number;
  username: string;
  email: string;
  roles: string[];
  credits: number;
}

export interface MessageResponse {
  message: string;
}

// Request DTOs
export interface SkillRequest {
  name: string;
  description: string;
  categoryId: number;
}

export interface UserProfileUpdateRequest {
  fullName?: string;
  bio?: string;
  profilePhotoUrl?: string;
}

export interface UserSkillRequest {
  skillId: number;
  relationType: SkillRelationType;
  proficiencyLevel: number;
  proficiencyDescription?: string;
}

export interface SessionRequestDto {
  recipientId: number;
  skillId: number;
  proposedDateTime: string;
  durationMinutes: number;
  message: string;
  teacherId: number;
  learnerId: number;
  meetingLink?: string;
}

export interface LearningSessionDto {
  sessionRequestId: number;
  startTime: string;
  endTime: string;
  credits: number;
}

export interface FeedbackDto {
  sessionId: number;
  reviewedId: number;
  rating: number;
  comment: string;
}

export interface ChatMessageDto {
  chatRoomId: number;
  content: string;
}

export interface CreditTopUpRequest {
  amount: number;
  paymentMethodId: string;
}

// API Response types
export interface ApiResponse<T> {
  data?: T;
  message?: string;
  error?: string;
}

export interface MatchmakingResult {
  user: User;
  skills: {
    skill: Skill;
    relationType: SkillRelationType;
    proficiencyLevel: number;
  }[];
}

export interface MutualMatch {
  user: User;
  commonSkills: {
    skill: Skill;
    requesterLevel: number;
    providerLevel: number;
  }[];
} 